import { useEffect, useState } from "react";
import { DataWrapper } from "@/common/components/dataWrapper";
import { Pagination } from "@/common/components/pagination";
import { PageClipSpinner } from "@/common/components/spinner";
import {
  PRODUCT_APPROVAL_HISTORY_PATH,
  PRODUCT_URL,
} from "@/common/constants";
import { IProductApprovalHistory } from "@/common/types";
import useQuery from "@/hooks/useQuery";
import { ApprovalHistoryWrapper, TimelineWrapper } from "./style";
import { HistoryEvent } from "./subs/history-event";
import { HistorySummary } from "./subs/history-summary";

export interface IApprovalHistoryPanelProps {
  productId?: string;
  /**
   * Bumped by the parent after an approval decision so the event it just
   * created is pulled into the timeline. The history is append-only, so a
   * refetch is the only way the panel learns about it.
   */
  refreshKey?: number;
}

/**
 * MED-653 (UC-PROD-004) — the product's immutable approval & update history,
 * shown alongside the approval screen so the admin can judge a repeated
 * edit-and-resubmit pattern before approving again.
 *
 * Read-only by construction: the backend exposes no write endpoint for history,
 * and every event is recorded by the system as the lifecycle action happens.
 */
export function ApprovalHistoryPanel({
  productId,
  refreshKey,
}: IApprovalHistoryPanelProps) {
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);

  const url =
    PRODUCT_URL +
    "/" +
    productId +
    PRODUCT_APPROVAL_HISTORY_PATH +
    `?per_page=${perPage}&page=${page}`;

  // Partial<>: useQuery seeds `data` with [] until the first response lands, so
  // neither `summary` nor `events` is there on the first render.
  const { data, error, loading, meta, refetch } = useQuery<
    Partial<IProductApprovalHistory>
  >(url, Boolean(productId));

  useEffect(() => {
    // Skips the initial render — useQuery already fetches on mount.
    if (refreshKey) refetch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const summary = data?.summary;
  const events = data?.events ?? [];

  return (
    <ApprovalHistoryWrapper>
      <div className="panel-header">
        <h2 className="panel-title">Approval &amp; Update History</h2>
        <span className="panel-subtitle">
          Every submission, decision, edit and resubmission recorded for this
          product, oldest first. System-generated and read-only.
        </span>
      </div>

      <DataWrapper
        data={events}
        noDataMessage="No approval history recorded for this product yet"
        loading={loading}
        error={error}
        customLoader={<PageClipSpinner message="Loading approval history..." />}
        failedMessage="Could not fetch the approval history. Check your network and click the button below to retry"
        onFailAction={() => refetch()}
      >
        <>
          {summary ? <HistorySummary summary={summary} /> : null}

          <TimelineWrapper>
            {events.map((event) => (
              <HistoryEvent key={event.id} event={event} />
            ))}
          </TimelineWrapper>
        </>
      </DataWrapper>

      <Pagination
        pageCount={meta?.total_pages}
        onNextPage={({ selected }) => setPage(selected)}
        onPerPageChange={(event: any) => setPerPage(event?.target?.value)}
      />
    </ApprovalHistoryWrapper>
  );
}
